/**
 * /hotels/booking/:reference — Booking confirmation page.
 *
 * Renders every required Hotelbeds-cert disclosure:
 *   • Booking reference (Hotelbeds' ID)
 *   • Hotel info + address
 *   • Guest info
 *   • Room + board + rate
 *   • Total price
 *   • Cancellation policy in destination local time
 *   • Rate comments
 *   • Payment statement (supplier + VAT + reference)
 *   • Powered by Hotelbeds
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft, MapPin, Calendar, Users, Info, Shield, Download, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PoweredByHotelbeds from "@/components/hotels/PoweredByHotelbeds";
import HotelCancelModal from "@/components/hotels/HotelCancelModal";

function formatLocalDeadline(iso, tz) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const opts = { dateStyle: "long", timeStyle: "short", timeZone: tz || undefined };
  try {
    return d.toLocaleString(undefined, opts);
  } catch {
    return d.toLocaleString();
  }
}

export default function HotelBookingConfirmationPage() {
  const { reference } = useParams();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const loadBooking = () => {
    setLoading(true);
    api.get(`/hotels/bookings/${encodeURIComponent(reference)}`)
      .then(({ data }) => setBooking(data))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  const isCancelled = (booking?.status || "").toUpperCase() === "CANCELLED";
  const voucherUrl = booking ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/hotels/bookings/${encodeURIComponent(booking.hotelbeds_reference)}/voucher.pdf` : "";

  const cp = booking?.cancellation_policies?.[0];
  const cancelDeadline = formatLocalDeadline(cp?.from, booking?.destination_timezone);
  const cancelFee = cp?.amount;

  return (
    <div data-testid="hotel-booking-confirmation" className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8 pt-6 pb-24">
        <button
          onClick={() => nav("/hotels")}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border-2 border-tb-navy/20 font-hand text-tb-navy/70 tb-tap text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> back to hotels
        </button>

        {loading ? (
          <p className="mt-16 text-center font-hand text-tb-navy/60">~ loading booking ~</p>
        ) : err ? (
          <div className="mt-6 rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-hand text-red-700 text-sm">{err}</div>
        ) : booking ? (
          <>
            <div className={"mt-6 rounded-2xl border-2 p-5 flex items-start gap-3 " + (isCancelled ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50")}>
              {isCancelled ? (
                <XCircle className="w-7 h-7 text-red-600 shrink-0" strokeWidth={2} />
              ) : (
                <CheckCircle2 className="w-7 h-7 text-emerald-600 shrink-0" strokeWidth={2} />
              )}
              <div className="flex-1">
                <h1 className="text-tb-navy text-2xl md:text-3xl leading-tight font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
                  {isCancelled ? "Booking cancelled" : "Booking confirmed"}
                </h1>
                <p className="mt-1 font-hand text-tb-navy/75 text-sm">
                  Reference: <span data-testid="hotel-confirmation-reference" className="font-mono font-semibold">{booking.hotelbeds_reference}</span>
                </p>
                {booking.supplier_name && (
                  <p data-testid="hotel-confirmation-supplier" className="mt-1 font-hand text-tb-navy/85 text-sm">
                    Booked through <b>{booking.supplier_name}</b>.
                  </p>
                )}
                {isCancelled && booking.cancellation_fee != null && (
                  <p className="mt-1 font-hand text-red-700 text-sm">
                    Cancellation fee applied: <b>{booking.cancellation_fee} {booking.cancellation_currency || booking.currency}</b>
                  </p>
                )}
                <div className="mt-2">
                  <PoweredByHotelbeds size="xs" />
                </div>
              </div>
              {!isCancelled && (
                <span data-testid="confirmation-status-pill" className="shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-hand bg-emerald-100 border border-emerald-300 text-emerald-800">
                  Confirmed
                </span>
              )}
              {isCancelled && (
                <span data-testid="confirmation-status-pill" className="shrink-0 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-hand bg-red-100 border border-red-300 text-red-800">
                  Cancelled
                </span>
              )}
            </div>

            <section className="mt-6 rounded-2xl border-2 border-tb-navy/15 bg-white p-5 md:p-6">
              <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
                {booking.hotel_name}
              </h2>
              {booking.hotel_address && (
                <p className="mt-1 font-hand text-tb-navy/70 text-sm flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {booking.hotel_address}
                </p>
              )}

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-tb-navy/60 mt-0.5" />
                  <div>
                    <div className="font-hand text-tb-navy/55 text-[10px] uppercase tracking-widest">Stay</div>
                    <div className="font-hand text-tb-navy text-sm">{booking.check_in} → {booking.check_out}</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="w-4 h-4 text-tb-navy/60 mt-0.5" />
                  <div>
                    <div className="font-hand text-tb-navy/55 text-[10px] uppercase tracking-widest">Lead guest</div>
                    <div className="font-hand text-tb-navy text-sm">{(booking.holder || {}).name} {(booking.holder || {}).surname}</div>
                    <div className="font-hand text-tb-navy/60 text-xs">{(booking.holder || {}).email}</div>
                  </div>
                </div>
              </div>

              {(booking.rooms || []).map((rm, i) => {
                const rate = (rm.rates || [])[0] || {};
                return (
                  <div key={i} className="mt-4 border-t border-tb-navy/15 pt-3">
                    <div className="font-hand text-tb-navy/70 text-xs uppercase tracking-widest">Room {i + 1}</div>
                    <div className="font-hand text-tb-navy text-sm">{rm.name || rm.code}</div>
                    <div className="mt-1 font-hand text-tb-navy/70 text-xs">
                      {rate.boardName || rate.boardCode} · {rate.paymentType || "AT_WEB"}
                    </div>
                  </div>
                );
              })}

              <div className="mt-5 pt-4 border-t border-tb-navy/15 flex items-end justify-between">
                <div className="font-hand text-tb-navy/70 text-sm">Total</div>
                <div data-testid="hotel-confirmation-total" className="font-serif text-tb-navy text-2xl" style={{ fontFamily: '"Playfair Display", serif' }}>
                  {booking.total_price} {booking.currency}
                </div>
              </div>
            </section>

            {/* Cancellation */}
            {cancelDeadline && (
              <section data-testid="hotel-confirmation-cancellation" className="mt-6 rounded-2xl border-2 border-tb-navy/25 bg-white p-5">
                <div className="flex items-center gap-2 font-hand text-tb-navy text-sm uppercase tracking-widest">
                  <Shield className="w-4 h-4" /> Cancellation policy
                </div>
                <p className="mt-2 font-hand text-tb-navy text-base">
                  Free cancellation until <b>{cancelDeadline}</b>{booking.destination_timezone ? ` (${booking.destination_timezone})` : ""}.
                  After that a cancellation fee of <b>{cancelFee} {booking.currency}</b> applies.
                </p>
              </section>
            )}

            {/* Rate comments */}
            {booking.rate_comments && (
              <section className="mt-6 rounded-2xl border-2 border-tb-navy/15 bg-white p-5 font-hand text-tb-navy/85 text-sm italic">
                {booking.rate_comments}
              </section>
            )}

            {/* Payment statement */}
            {booking.payment_statement && (
              <section data-testid="hotel-confirmation-payment-statement" className="mt-6 rounded-xl border border-tb-navy/20 bg-white px-4 py-3 flex items-start gap-2 font-hand text-tb-navy/70 text-xs">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{booking.payment_statement}</span>
              </section>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={voucherUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hotel-confirmation-download-voucher"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-tb-navy-deep text-tb-gold-bright border-2 border-tb-gold font-hand text-base tb-tap hover:brightness-110"
              >
                <Download className="w-4 h-4" /> Download voucher (PDF)
              </a>
              <button
                type="button"
                onClick={() => nav("/hotels/mine")}
                data-testid="hotel-confirmation-view-trips"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-tb-navy border-2 border-tb-navy/15 font-hand text-base tb-tap hover:border-tb-navy"
              >
                View my hotel bookings
              </button>
              {!isCancelled && (
                <button
                  type="button"
                  data-testid="hotel-confirmation-cancel"
                  onClick={() => setCancelOpen(true)}
                  className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-red-700 border-2 border-red-200 font-hand text-base tb-tap hover:border-red-400"
                >
                  <XCircle className="w-4 h-4" /> Cancel booking
                </button>
              )}
            </div>

            {cancelOpen && booking && (
              <HotelCancelModal
                reference={booking.hotelbeds_reference}
                hotelName={booking.hotel_name}
                onClose={() => setCancelOpen(false)}
                onCancelled={() => { setCancelOpen(false); loadBooking(); }}
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
